import { Workflow } from '@botpress/client'
import { DateTime } from 'luxon'
import { create } from 'zustand'
import { getApiClient } from '../../../stores'

// TODO: This has been copied from stratus and needs refactoring

export type CrawlTaskProgress = {
  eventId: string
  workflowId: string
  dsId: string
  processed: number
  total: number
  status: Workflow['status']
  failureReason: Workflow['failureReason']
  isActive?: boolean
  errors?: string[]
}

type BaseCrawlState = {
  isFinished?: boolean
  lastProcessedIndex: number
  continuousTimeouts: number // Increments when a timeout occurs, after 3 consecutive timeouts there is most probably something wrong
  errors?: string[]
}

export type CrawlFromFileState = BaseCrawlState & {
  totalUrls: number
}

type CrawlTaskStore = {
  tasks: CrawlTaskProgress[]
  trackCrawlEvent: (props: WorkflowEventProps) => Promise<void>
  cleanInactive: () => void
}

type WorkflowEventProps = {
  eventId: string
  botId: string
  workspaceId: string
}

type WebsiteCrawlerTask = {
  dsId: string
  pageUrl: string
  urls: string[]
  kbId: string
}

export const POLL_INTERVAL_MS = 3000
const MAX_FETCH_ATTEMPTS = 6

const eventIdWorkflowMap = new Map<string, string>()

export const getWorkflowFromEventId = async ({ eventId, botId, workspaceId }: WorkflowEventProps) => {
  let workflowId = eventIdWorkflowMap.get(eventId)
  const client = getApiClient({ botId, workspaceId })
  if (!workflowId) {
    const {
      workflows: [workflow],
    } = await client.listWorkflows({ tags: { eventId } })

    if (workflow) {
      workflowId = workflow.id
    }
  }

  if (workflowId) {
    eventIdWorkflowMap.set(eventId, workflowId)
    const { workflow } = await client.getWorkflow({ id: workflowId })
    return workflow
  }
}

export const getCrawlTaskProgress = async (props: WorkflowEventProps) => {
  const workflow = await getWorkflowFromEventId(props)
  if (!workflow) {
    return
  }

  const workflowInfo = {
    dsId: workflow.input.dataSourceId,
    workflowId: workflow.id,
    status: workflow.status,
    failureReason: workflow.failureReason,
    isActive: ['pending', 'in_progress'].includes(workflow.status),
  }

  try {
    const client = getApiClient({ botId: props.botId, workspaceId: props.workspaceId })
    const stateName = `website-crawling-${workflow.input.dataSourceId}`
    const { state } = await client.getState({ id: workflow.id, type: 'workflow', name: stateName })
    const payload = state.payload as CrawlFromFileState
    return {
      ...workflowInfo,
      eventId: props.eventId,
      processed: payload.lastProcessedIndex + 1,
      total: payload.totalUrls,
      errors: payload.errors,
    }
  } catch (err) {
    return {
      ...workflowInfo,
      eventId: props.eventId,
      processed: 0,
      total: -1,
    }
  }
}

const getCrawlTaskProgressWithRetry = async (props: WorkflowEventProps) => {
  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
    try {
      const task = await getCrawlTaskProgress(props)
      if (task) {
        return task
      }
      await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS))
    } catch (err) {
      console.error('Error fetching crawl task progress:', err)
      if (attempt >= MAX_FETCH_ATTEMPTS) {
        console.error(`Crawl task progress not found after ${MAX_FETCH_ATTEMPTS} attempts`, err)
        return
      }
      await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS))
    }
  }
}

export const startCrawlNewUrls = async ({
  botId,
  workspaceId,
  ...props
}: WebsiteCrawlerTask & { botId: string; workspaceId: string }) => {
  const client = getApiClient({ botId, workspaceId })

  const { file } = await client.uploadFile({
    tags: {
      source: 'website-indexing',
    },
    key: `indexing-task-${props.dsId}-${DateTime.now().toFormat('yyyyMMdd-HHmm')}.json`,
    content: new Blob([JSON.stringify(props)], { type: 'application/json' }) as any,
  })

  const payload: {
    dataSourceId?: string
    pageUrl?: string
    crawlTaskFileId?: string
  } = {
    dataSourceId: props.dsId,
    pageUrl: props.pageUrl,
    crawlTaskFileId: file.id,
  }

  const { event } = await client.createEvent({ type: 'websiteCrawl', payload })

  setTimeout(() => {
    void useCrawlTaskStore.getState().trackCrawlEvent({ eventId: event.id, botId, workspaceId })
  }, 1000)

  return event.id
}

const activeIntervals = new Map<string, NodeJS.Timeout>()

const createStore = () => {
  const store = create<CrawlTaskStore>((set, get) => ({
    tasks: [],
    trackCrawlEvent: async (props) => {
      const task = await getCrawlTaskProgressWithRetry(props)
      if (!task) {
        return
      }

      set((state) => ({ tasks: [...state.tasks.filter((x) => x.eventId !== props.eventId), task] }))

      if (!task.isActive || activeIntervals.has(props.eventId)) {
        return
      }

      const intervalId = setInterval(async () => {
        try {
          const updatedTask = await getCrawlTaskProgressWithRetry(props)

          if (updatedTask) {
            set((state) => ({
              tasks: [...state.tasks.filter((x) => x.eventId !== props.eventId), updatedTask],
            }))

            if (!updatedTask.isActive) {
              clearInterval(intervalId)
              activeIntervals.delete(props.eventId)
            }
          }
        } catch (err) {
          console.error('Error polling tasks:', err)
          clearInterval(intervalId)
          activeIntervals.delete(props.eventId)
        }
      }, POLL_INTERVAL_MS)
      activeIntervals.set(props.eventId, intervalId)
    },
    cleanInactive: () => set((state) => ({ tasks: state.tasks.filter((x) => x.isActive) })),
  }))
  return store
}

export const useCrawlTaskStore = createStore()
